import React, { useState, useEffect } from "react";
import "./ShoppingCart.css";
import { useSelector, useDispatch } from "react-redux";
import {
  removeFromCart,
  updateQuantity,
  clearCart,
  selectCartTotalAmount,
} from "../../Features/Cart/cartSlice";

import { MdOutlineClose } from "react-icons/md";

import { Link, useNavigate } from "react-router-dom";

import success from "../../Assets/success.png";
import { getCustomerToken, customerApi, getImageUrl, API_BASE } from "../../api/client";
import {
  getCurrentPosition,
  reverseGeocodeLatLng,
  saveDeliveryLocationSnapshot,
  getDeliveryLocationSnapshot,
} from "../../utils/locationHelper";
import Seo from "../../Components/Seo/Seo";
import { useSiteSeo } from "../../context/SiteSeoContext";
import { usePrerenderReady } from "../../utils/prerenderReady";
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID || "";

const CHECKOUT_STORAGE_KEY = "checkout_data";

const ShoppingCart = () => {
  const navigate = useNavigate();
  const cartItems = useSelector((state) => state.cart.items);
  const dispatch = useDispatch();
  const totalPrice = useSelector(selectCartTotalAmount);
  const siteCtx = useSiteSeo();
  const titleSuffix =
    siteCtx.organization_name && String(siteCtx.organization_name).trim()
      ? ` | ${String(siteCtx.organization_name).trim()}`
      : " | Gem Fashion Studios";
  usePrerenderReady(true);

  const [activeTab, setActiveTab] = useState("cartTab1");
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [address, setAddress] = useState({ line1: "", line2: "", city: "", state: "", pincode: "" });
  const [couponCode, setCouponCode] = useState("");
  const [orderError, setOrderError] = useState("");
  const [lastOrderId, setLastOrderId] = useState(null);
  const [lastOrderTotal, setLastOrderTotal] = useState(null);
  const [cartTotals, setCartTotals] = useState(null);
  const [cartTotalsLoading, setCartTotalsLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationMessage, setLocationMessage] = useState("");

  // Fetch cart totals from backend (GST, shipping, deposit) when cart or address/coupon change
  useEffect(() => {
    const subtotalPaise = Math.round((totalPrice || 0) * 100);
    if (subtotalPaise <= 0) {
      setCartTotals(null);
      return;
    }
    setCartTotalsLoading(true);
    fetch(`${API_BASE}/api/cart/calculate-totals`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        subtotal_paise: subtotalPaise,
        pincode: address.pincode?.trim() || undefined,
        coupon_code: couponCode?.trim() || undefined,
        items: cartItems.map((i) => ({
          productId: i.productId ?? i.productID,
          quantity: i.quantity || 1,
        })),
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setCartTotals(data);
      })
      .catch(() => setCartTotals(null))
      .finally(() => setCartTotalsLoading(false));
  }, [totalPrice, address.pincode, couponCode, cartItems]);

  // Prefill address from site-load location snapshot (if any)
  useEffect(() => {
    const snap = getDeliveryLocationSnapshot();
    if (!snap) return;
    setAddress((a) => ({
      ...a,
      line1: a.line1?.trim() ? a.line1 : snap.line1 || "",
      pincode: a.pincode?.trim() ? a.pincode : snap.pincode || "",
      city: a.city?.trim() ? a.city : snap.city || "",
      state: a.state?.trim() ? a.state : snap.state || "",
    }));
  }, []);

  // Prefill contact and address when customer is logged in
  useEffect(() => {
    if (!getCustomerToken()) return;
    customerApi("/api/customer/me")
      .then((profile) => {
        setContact((c) => ({
          name: profile.name || c.name,
          email: profile.email || c.email,
          phone: profile.phone || c.phone,
        }));
      })
      .catch(() => {});
    customerApi("/api/customer/addresses")
      .then((addrs) => {
        const defaultAddr = addrs.find((a) => a.is_default) || addrs[0];
        if (defaultAddr)
          setAddress({
            line1: defaultAddr.line1 || "",
            line2: defaultAddr.line2 || "",
            city: defaultAddr.city || "",
            state: defaultAddr.state || "",
            pincode: defaultAddr.pincode || "",
          });
      })
      .catch(() => {});
  }, []);

  const handleTabClick = (tab) => {
    if (tab === "cartTab1" || cartItems.length > 0) {
      setActiveTab(tab);
    }
  };

  const formatSelectedAttributes = (selectedAttributes) => {
    if (!selectedAttributes || typeof selectedAttributes !== "object") return null;
    const entries = Object.entries(selectedAttributes).filter(([, v]) => v != null && String(v).trim());
    if (entries.length === 0) return null;
    return entries.map(([k, v]) => `${k}: ${v}`).join(" · ");
  };

  const handleQuantityChange = (item, quantity) => {
    if (quantity >= 1 && quantity <= 20) {
      dispatch(updateQuantity({
        productID: item.productID ?? item.productId,
        productId: item.productId ?? item.productID,
        rent_days: item.rent_days,
        event_date: item.event_date,
        selectedAttributes: item.selectedAttributes ?? null,
        quantity,
      }));
    }
  };

  const eventDateForOrder = cartItems.length > 0
    ? (cartItems.find((i) => i.event_date)?.event_date || "")
    : "";

  const handleUseMyLocation = async () => {
    setOrderError("");
    setLocationMessage("");
    setLocationLoading(true);
    try {
      const { latitude, longitude } = await getCurrentPosition();
      const geo = await reverseGeocodeLatLng(latitude, longitude);
      saveDeliveryLocationSnapshot({
        lat: latitude,
        lng: longitude,
        pincode: geo.pincode,
        city: geo.city,
        state: geo.state,
        line1: geo.line1,
      });
      setAddress((a) => ({
        ...a,
        line1: a.line1?.trim() ? a.line1 : geo.line1,
        pincode: geo.pincode || a.pincode,
        city: geo.city || a.city,
        state: geo.state || a.state,
      }));
      setLocationMessage(
        "Location saved. Check street address and pincode, then continue."
      );
    } catch (e) {
      setOrderError(e.message || "Could not use your location.");
    } finally {
      setLocationLoading(false);
    }
  };

  const proceedToPayment = () => {
    setOrderError("");
    setLocationMessage("");
    const missingEvent = cartItems.find((i) => !(i.event_date && i.event_date.trim()));
    if (missingEvent) {
      setOrderError("Please set event date for all items. Open each product and select your event date.");
      return;
    }
    if (!eventDateForOrder) {
      setOrderError("Event date is required.");
      return;
    }
    if (!contact.name?.trim() || !contact.email?.trim() || !contact.phone?.trim()) {
      setOrderError("Please fill in name, email and phone.");
      return;
    }
    if (!address.line1?.trim() || !address.city?.trim() || !address.pincode?.trim()) {
      setOrderError("Please fill in address (street, city, pincode).");
      return;
    }
    if (!getCustomerToken()) {
      sessionStorage.setItem(
        CHECKOUT_STORAGE_KEY,
        JSON.stringify({
          address: { ...address },
          contact: { ...contact },
          couponCode,
        })
      );
      navigate(`/loginSignUp?redirect=${encodeURIComponent("/payment")}`);
      return;
    }
    sessionStorage.setItem(
      CHECKOUT_STORAGE_KEY,
      JSON.stringify({
        address: { ...address },
        contact: { ...contact },
        couponCode,
      })
    );
    navigate("/payment");
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // current Date

  const currentDate = new Date();

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const orderNumber = Math.floor(Math.random() * 100000);

  return (
    <>
      <Seo
        title={`Shopping cart${titleSuffix}`}
        description="Review your rentals, dates, and delivery details before Razorpay checkout."
        canonicalPath="/cart"
        noindex
      />
      <div className="shoppingCartSection">
        <h2>Cart</h2>

        <div className="shoppingCartTabsContainer">
          <div className={`shoppingCartTabs ${activeTab}`}>
            <button
              className={activeTab === "cartTab1" ? "active" : ""}
              onClick={() => handleTabClick("cartTab1")}
            >
              <div className="shoppingCartTabsNumber">
                <h3>01</h3>
                <div className="shoppingCartTabsHeading">
                  <h3>Rental Bag</h3>
                  <p>Manage Your Items List</p>
                </div>
              </div>
            </button>
            <button
              className={activeTab === "cartTab2" ? "active" : ""}
              onClick={() => handleTabClick("cartTab2")}
              disabled={cartItems.length === 0}
            >
              <div className="shoppingCartTabsNumber">
                <h3>02</h3>
                <div className="shoppingCartTabsHeading">
                  <h3>Shipping and Checkout</h3>
                  <p>Checkout Your Items List</p>
                </div>
              </div>
            </button>
            <button
              className={activeTab === "cartTab3" ? "active" : ""}
              onClick={() => handleTabClick("cartTab3")}
              disabled={cartItems.length === 0}
            >
              <div className="shoppingCartTabsNumber">
                <h3>03</h3>
                <div className="shoppingCartTabsHeading">
                  <h3>Confirmation</h3>
                  <p>Review And Submit Your Order</p>
                </div>
              </div>
            </button>
          </div>
          <div className="shoppingCartTabsContent">
            {/* tab1 */}
            {activeTab === "cartTab1" && (
              <div className="shoppingBagSection">
                <div className="shoppingBagTableSection">
                  {/* For Desktop Devices */}
                  <table className="shoppingBagTable">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th></th>
                        <th>Price</th>
                        <th>Quantity</th>
                        <th>Subtotal</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cartItems.length > 0 ? (
                        cartItems.map((item) => (
                          <tr key={`${item.productID ?? item.productId}-${item.rent_days ?? 1}-${item.event_date ?? ""}-${JSON.stringify(item.selectedAttributes || {})}`}>
                            <td data-label="Product">
                              <div className="shoppingBagTableImg">
                                <Link to={`/product/${item.productID ?? item.productId}`} onClick={scrollToTop}>
                                  <img src={getImageUrl(item.frontImg) || item.frontImg} alt="" />
                                </Link>
                              </div>
                            </td>
                            <td data-label="">
                              <div className="shoppingBagTableProductDetail">
                                <Link to={`/product/${item.productID ?? item.productId}`} onClick={scrollToTop}>
                                  <h4>{item.productName}</h4>
                                </Link>
                                {formatSelectedAttributes(item.selectedAttributes) && (
                                  <p style={{ fontSize: "0.85rem", color: "#666", marginTop: "0.25rem" }}>
                                    {formatSelectedAttributes(item.selectedAttributes)}
                                  </p>
                                )}
                                <p>{item.productReviews}</p>
                              </div>
                            </td>
                            <td
                              data-label="Price"
                              style={{ textAlign: "center" }}
                            >
                              ₹{item.productPrice}
                            </td>
                            <td data-label="Quantity">
                              <div className="ShoppingBagTableQuantity">
                                <button
                                  onClick={() =>
                                    handleQuantityChange(item, item.quantity - 1)
                                  }
                                >
                                  -
                                </button>
                                <input
                                  type="text"
                                  min="1"
                                  max="20"
                                  value={item.quantity}
                                  onChange={(e) =>
                                    handleQuantityChange(item, parseInt(e.target.value, 10) || 1)
                                  }
                                />
                                <button
                                  onClick={() =>
                                    handleQuantityChange(item, item.quantity + 1)
                                  }
                                >
                                  +
                                </button>
                              </div>
                            </td>
                            <td data-label="Subtotal">
                              <p
                                style={{
                                  textAlign: "center",
                                  fontWeight: "500",
                                }}
                              >
                                ₹{item.quantity * item.productPrice}
                              </p>
                            </td>
                            <td data-label="">
                              <MdOutlineClose
                                onClick={() =>
                                  dispatch(removeFromCart({ productId: item.productID ?? item.productId, rent_days: item.rent_days, event_date: item.event_date, selectedAttributes: item.selectedAttributes }))
                                }
                              />
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6">
                            <div className="shoppingCartEmpty">
                              <span>Your cart is empty!</span>
                              <Link to="/shop" onClick={scrollToTop}>
                                <button>Browse Rentals</button>
                              </Link>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <th
                        colSpan="6"
                        className="shopCartFooter"
                        style={{
                          borderBottom: "none",
                          padding: "20px 0px",
                        }}
                      >
                        {cartItems.length > 0 && (
                          <div className="shopCartFooterContainer">
                            <form>
                              <input
                                type="text"
                                placeholder="Coupon Code"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value)}
                              />
                              <button type="button">Apply at checkout</button>
                            </form>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                              }}
                              className="shopCartFooterbutton"
                            >
                              Update Cart
                            </button>
                          </div>
                        )}
                      </th>
                    </tfoot>
                  </table>

                  {/* For Mobile devices */}

                  <div className="shoppingBagTableMobile">
                    {cartItems.length > 0 ? (
                      <>
                        {cartItems.map((item) => (
                          <div key={`${item.productID ?? item.productId}-${item.rent_days ?? 1}-${item.event_date ?? ""}-${JSON.stringify(item.selectedAttributes || {})}`}>
                            <div className="shoppingBagTableMobileItems">
                              <div className="shoppingBagTableMobileItemsImg">
                                <Link to={`/product/${item.productID ?? item.productId}`} onClick={scrollToTop}>
                                  <img src={getImageUrl(item.frontImg) || item.frontImg} alt="" />
                                </Link>
                              </div>
                              <div className="shoppingBagTableMobileItemsDetail">
                                <div className="shoppingBagTableMobileItemsDetailMain">
                                  <Link to={`/product/${item.productID ?? item.productId}`} onClick={scrollToTop}>
                                    <h4>{item.productName}</h4>
                                  </Link>
                                  {formatSelectedAttributes(item.selectedAttributes) && (
                                    <p style={{ fontSize: "0.8rem", color: "#666", marginTop: "0.2rem" }}>
                                      {formatSelectedAttributes(item.selectedAttributes)}
                                    </p>
                                  )}
                                  <p>{item.productReviews}</p>
                                  <div className="shoppingBagTableMobileQuantity">
                                    <button
                                      onClick={() =>
                                        handleQuantityChange(item, item.quantity - 1)
                                      }
                                    >
                                      -
                                    </button>
                                    <input
                                      type="text"
                                      min="1"
                                      max="20"
                                      value={item.quantity}
                                      onChange={(e) =>
                                        handleQuantityChange(item, parseInt(e.target.value, 10) || 1)
                                      }
                                    />
                                    <button
                                      onClick={() =>
                                        handleQuantityChange(item, item.quantity + 1)
                                      }
                                    >
                                      +
                                    </button>
                                  </div>
                                  <span>₹{item.productPrice}</span>
                                </div>
                                <div className="shoppingBagTableMobileItemsDetailTotal">
                                  <MdOutlineClose
                                    size={20}
                                    onClick={() =>
                                      dispatch(removeFromCart({ productId: item.productID ?? item.productId, rent_days: item.rent_days, event_date: item.event_date, selectedAttributes: item.selectedAttributes }))
                                    }
                                  />
                                  <p>₹{item.quantity * item.productPrice}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                        <div className="shopCartFooter">
                          <div className="shopCartFooterContainer">
                            <form>
                              <input
                                type="text"
                                placeholder="Coupon Code"
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value)}
                              />
                              <button type="button">Apply at checkout</button>
                            </form>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                              }}
                              className="shopCartFooterbutton"
                            >
                              Update Cart
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="shoppingCartEmpty">
                        <span>Your cart is empty!</span>
                        <Link to="/shop" onClick={scrollToTop}>
                          <button>Browse Rentals</button>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
                <div className="shoppingBagTotal">
                  <h3>Cart Totals</h3>
                  <table className="shoppingBagTotalTable">
                    <tbody>
                      <tr>
                        <th>Subtotal</th>
                        <td>₹{totalPrice.toFixed(2)}</td>
                      </tr>
                      {cartTotals && (
                        <>
                          <tr>
                            <th>{cartTotals.deposit_label || "Deposit"}</th>
                            <td>₹{((cartTotals.deposit_paise || 0) / 100).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <th>{cartTotals.shipping_label || "Shipping"}</th>
                            <td>
                              <div className="shoppingBagTotalTableCheck">
                                <p>₹{((cartTotals.shipping_paise || 0) / 100).toFixed(2)}</p>
                                <p>Shipping to {address.pincode ? `${address.pincode.slice(0, 6)}..` : "—"}</p>
                                <p onClick={scrollToTop} style={{ cursor: "pointer" }}>
                                  CHANGE ADDRESS
                                </p>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <th>{cartTotals.gst_label || "GST"}</th>
                            <td>₹{((cartTotals.gst_paise || 0) / 100).toFixed(2)}</td>
                          </tr>
                        </>
                      )}
                      <tr>
                        <th>Total</th>
                        <td>
                          ₹{cartTotals
                            ? ((cartTotals.total_paise || 0) / 100).toFixed(2)
                            : cartTotalsLoading
                              ? "—"
                              : totalPrice.toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <button
                    onClick={() => {
                      handleTabClick("cartTab2");
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    disabled={cartItems.length === 0}
                  >
                    Proceed to Checkout
                  </button>
                </div>
              </div>
            )}

            {/* tab2 */}
            {activeTab === "cartTab2" && (
              <div className="checkoutSection">
                {orderError && (
                  <p style={{ color: "#c00", marginBottom: "1rem" }}>{orderError}</p>
                )}
                <div className="checkoutDetailsSection">
                  <h4>Billing Details</h4>
                  <div className="checkoutLocationBlock">
                    <p className="checkoutLocationTitle">Delivery location</p>
                    <p className="checkoutLocationText">
                      Allow location access so we can fill city, state and pincode for shipping. Your browser will ask
                      for permission.
                    </p>
                    <button
                      type="button"
                      className="checkoutLocationBtn"
                      onClick={handleUseMyLocation}
                      disabled={locationLoading}
                    >
                      {locationLoading ? "Getting location…" : "Use my current location"}
                    </button>
                    <p className="checkoutLocationAttribution" title="OpenStreetMap Nominatim">
                      Address lookup uses OpenStreetMap data (© OpenStreetMap contributors).
                    </p>
                  </div>
                  {locationMessage && (
                    <p className="checkoutLocationSuccess">{locationMessage}</p>
                  )}
                  <div className="checkoutDetailsForm">
                    <form onSubmit={(e) => e.preventDefault()}>
                      <input
                        type="text"
                        placeholder="Full Name *"
                        value={contact.name}
                        onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))}
                      />
                      <input
                        type="email"
                        placeholder="Email *"
                        value={contact.email}
                        onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
                      />
                      <input
                        type="text"
                        placeholder="Phone *"
                        value={contact.phone}
                        onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))}
                      />
                      <input
                        type="text"
                        placeholder="Street Address *"
                        value={address.line1}
                        onChange={(e) => setAddress((a) => ({ ...a, line1: e.target.value }))}
                      />
                      <input
                        type="text"
                        placeholder="Address line 2 (optional)"
                        value={address.line2}
                        onChange={(e) => setAddress((a) => ({ ...a, line2: e.target.value }))}
                      />
                      <input
                        type="text"
                        placeholder="City *"
                        value={address.city}
                        onChange={(e) => setAddress((a) => ({ ...a, city: e.target.value }))}
                      />
                      <input
                        type="text"
                        placeholder="State (optional)"
                        value={address.state}
                        onChange={(e) => setAddress((a) => ({ ...a, state: e.target.value }))}
                      />
                      <input
                        type="text"
                        placeholder="Pincode *"
                        value={address.pincode}
                        onChange={(e) => setAddress((a) => ({ ...a, pincode: e.target.value }))}
                      />
                    </form>
                  </div>
                </div>
                <div className="checkoutPaymentSection">
                  <div className="checkoutTotalContainer">
                    <h3>Your Order</h3>
                    <div className="checkoutItems">
                      <table>
                        <thead>
                          <tr>
                            <th>PRODUCTS</th>
                            <th>SUBTOTALS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cartItems.map((items) => (
                            <tr key={items.productId || items.productName}>
                              <td>
                                {items.productName} x {items.quantity}
                              </td>
                              <td>₹{items.productPrice * items.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="checkoutTotal">
                      <table>
                        <tbody>
                          <tr>
                            <th>Subtotal</th>
                            <td>₹{totalPrice.toFixed(2)}</td>
                          </tr>
                          {cartTotals && (
                            <>
                              <tr>
                                <th>{cartTotals.deposit_label || "Deposit"}</th>
                                <td>₹{((cartTotals.deposit_paise || 0) / 100).toFixed(2)}</td>
                              </tr>
                              <tr>
                                <th>{cartTotals.shipping_label || "Shipping"}</th>
                                <td>₹{((cartTotals.shipping_paise || 0) / 100).toFixed(2)}</td>
                              </tr>
                              <tr>
                                <th>{cartTotals.gst_label || "GST"}</th>
                                <td>₹{((cartTotals.gst_paise || 0) / 100).toFixed(2)}</td>
                              </tr>
                            </>
                          )}
                          <tr>
                            <th>Total</th>
                            <td>
                              ₹{cartTotals
                                ? ((cartTotals.total_paise || 0) / 100).toFixed(2)
                                : cartTotalsLoading ? "—" : totalPrice.toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="checkoutPaymentContainer">
                    {!getCustomerToken() && (
                      <p className="checkoutLoginRequired">
                        <Link to={`/loginSignUp?redirect=${encodeURIComponent("/payment")}`} onClick={scrollToTop}>
                          Sign in
                        </Link>{" "}
                        is required to place an order. Your cart and this form will be kept.
                      </p>
                    )}
                    <p className="checkoutPaymentNote">
                      After filling your details, click below to go to payment and complete checkout with Razorpay (UPI, cards, net banking).
                    </p>
                    <div className="policyText">
                      Your personal data will be used to process your order,
                      support your experience throughout this website, and for
                      other purposes described in our{" "}
                      <Link to="/privacy-policy" onClick={scrollToTop}>
                        Privacy Policy
                      </Link>
                      .
                    </div>
                  </div>
                  <input
                    type="text"
                    placeholder="Coupon code (optional)"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    style={{ marginBottom: "0.5rem", padding: "0.5rem" }}
                  />
                  <button
                    onClick={proceedToPayment}
                    disabled={cartItems.length === 0}
                  >
                    {getCustomerToken() ? "Proceed to Payment" : "Sign in & proceed to payment"}
                  </button>
                </div>
              </div>
            )}

            {/* tab3 */}
            {activeTab === "cartTab3" && (
              <div className="orderCompleteSection">
                <div className="orderComplete">
                  <div className="orderCompleteMessage">
                    <div className="orderCompleteMessageImg">
                      <img src={success} alt="" />
                    </div>
                    <h3>Your order is completed!</h3>
                    <p>Thank you. Your order has been received.</p>
                  </div>
                  <div className="orderInfo">
                    <div className="orderInfoItem">
                      <p>Order Number</p>
                      <h4>{lastOrderId != null ? lastOrderId : orderNumber}</h4>
                    </div>
                    <div className="orderInfoItem">
                      <p>Date</p>
                      <h4>{formatDate(currentDate)}</h4>
                    </div>
                    <div className="orderInfoItem">
                      <p>Total</p>
                      <h4>₹{(lastOrderTotal != null ? lastOrderTotal : totalPrice).toFixed(2)}</h4>
                    </div>
                    <div className="orderInfoItem">
                      <p>Payment Method</p>
                      <h4>{lastOrderId != null ? "Razorpay" : "—"}</h4>
                    </div>
                  </div>
                  <div className="orderTotalContainer">
                    <h3>Order Details</h3>
                    <div className="orderItems">
                      <table>
                        <thead>
                          <tr>
                            <th>PRODUCTS</th>
                            <th>SUBTOTALS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cartItems.map((items) => (
                            <tr key={items.productId || items.productName}>
                              <td>
                                {items.productName} x {items.quantity}
                              </td>
                              <td>₹{items.productPrice * items.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="orderTotal">
                      <table>
                        <tbody>
                          <tr>
                            <th>Subtotal</th>
                            <td>₹{totalPrice.toFixed(2)}</td>
                          </tr>
                          {cartTotals && (
                            <>
                              <tr>
                                <th>{cartTotals.deposit_label || "Deposit"}</th>
                                <td>₹{((cartTotals.deposit_paise || 0) / 100).toFixed(2)}</td>
                              </tr>
                              <tr>
                                <th>{cartTotals.shipping_label || "Shipping"}</th>
                                <td>₹{((cartTotals.shipping_paise || 0) / 100).toFixed(2)}</td>
                              </tr>
                              <tr>
                                <th>{cartTotals.gst_label || "GST"}</th>
                                <td>₹{((cartTotals.gst_paise || 0) / 100).toFixed(2)}</td>
                              </tr>
                            </>
                          )}
                          <tr>
                            <th>Total</th>
                            <td>
                              ₹{cartTotals
                                ? ((cartTotals.total_paise || 0) / 100).toFixed(2)
                                : cartTotalsLoading ? "—" : totalPrice.toFixed(2)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ShoppingCart;
